import { useState, useEffect } from 'react';

export interface FooterLink {
  id: number;
  type: string;
  name: string;
  name_en: string;
  name_ar: string;
  url: string;
  order: number;
  icon?: string;
  opensNewTab: boolean;
}

export interface FooterSettings {
  // Visibility Controls
  footer_enabled: boolean;
  company_section_enabled: boolean;
  services_section_enabled: boolean;
  quicklinks_section_enabled: boolean;
  contact_section_enabled: boolean;
  social_links_enabled: boolean;
  bottom_bar_enabled: boolean;
  wave_separator_enabled: boolean;
  animations_enabled: boolean;

  // Content - Company Section
  company_description_en: string;
  company_description_ar: string;
  company_logo_enabled: boolean;

  // Content - Section Headings
  services_heading_en: string;
  services_heading_ar: string;
  quicklinks_heading_en: string;
  quicklinks_heading_ar: string;
  contact_heading_en: string;
  contact_heading_ar: string;

  // Content - Contact Information
  footer_email: string;
  footer_support_hours_en: string;
  footer_support_hours_ar: string;
  whatsapp_contact_enabled: boolean;

  // Content - Copyright & Legal
  copyright_text_en: string;
  copyright_text_ar: string;
  show_legal_links: boolean;

  // Design Settings
  background_gradient_start: string;
  background_gradient_end: string;
  text_primary_color: string;
  text_secondary_color: string;

  // Services Configuration
  display_all_countries: boolean;
  selected_countries: string[];

  // Social Media Toggles
  show_twitter: boolean;
  show_linkedin: boolean;
  show_facebook: boolean;
  show_whatsapp_social: boolean;
}

export interface FooterData {
  settings: FooterSettings | null;
  quickLinks: FooterLink[];
  legalLinks: FooterLink[];
  loading: boolean;
  error: string | null;
}

export const useFooterSettings = (language: string = 'en'): FooterData => {
  const [data, setData] = useState<FooterData>({
    settings: null,
    quickLinks: [],
    legalLinks: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        // Fetch footer settings and links in parallel
        const [settingsRes, linksRes] = await Promise.all([
          fetch('https://patrixglobal.com/backend/api/settings/?category=footer'),
          fetch(`https://patrixglobal.com/backend/api/settings/footer-links?language=${language}`),
        ]);

        const [settingsData, linksData] = await Promise.all([
          settingsRes.json(),
          linksRes.json(),
        ]);

        if (!settingsData.success || !linksData.success) {
          throw new Error('Failed to fetch footer data');
        }

        // Parse settings
        const settings = settingsData.data as FooterSettings;

        // Parse links by type
        const allLinks = linksData.data as FooterLink[];
        const quickLinks = allLinks.filter(link => link.type === 'quick_link');
        const legalLinks = allLinks.filter(link => link.type === 'legal_link');

        setData({
          settings,
          quickLinks,
          legalLinks,
          loading: false,
          error: null,
        });
      } catch (error) {        setData(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load footer data',
        }));
      }
    };

    fetchFooterData();
  }, [language]);

  return data;
};